<template>
    <div class="content">
        <div class="container-fluid">
            <transition name="fade" mode="out-in">
                <APILoading v-if="carrierDestination === undefined || carrier === undefined || currencies === undefined"
                            :text="$t('CarrierDestination.loadingCarrierDestination')"
                            :color="$data.$colors.billing" key="loading"/>
                <ResourceNotFound v-else-if="carrierDestination === null || carrier === null"
                                  key="not-found" category="billing"/>
                <div v-else key="carrier-destination" id="carrier-destination">
                    <router-link class="font-italic" :to="`/billing/carrier/${carrierDestination.carrierId}`">
                        <i class="fa fa-chevron-circle-left"/>
                        {{ `${$t("CarrierDestination.backToCarrier")} - ${carrierDestination.carrier.name}` }}
                    </router-link>
                    <PageTitle class="mt-2" :title="pageTitle">
                        <template v-slot:left>
                            <i class="fa fa-flag fa-3x text-billing"/>
                        </template>
                    </PageTitle>
                    <div class="row">
                        <div v-if="carrierDestination.carrierAreaId" class="col-12 d-flex align-items-center alert alert-info">
                            <span class="fa fa-info-circle fa-2x mr-2"/>
                            <span>
                                {{ $t("CarrierDestination.thisDestinationHasArea") }}
                                <br/>
                                <router-link class="font-bold text-info" :to="`/billing/carrier-area/${carrierDestination.carrierAreaId}`">
                                    {{ $t("CarrierDestination.toChangePricesGoToArea") }}
                                    <i class="fas fa-link"/>
                                </router-link>
                            </span>
                        </div>
                        <CarrierInfoCard @editCarrier="editCarrier" :carrier="carrier"/>
                        <CarrierDestinationInfoCard @editCarrierDestination="editCarrierDestination" :carrier="carrier"
                                                    :carrier-destination="carrierDestination" :countries="countries"/>
                    </div>
                    <div class="row">
                        <div class="col-lg-12">
                            <div class="card card-tabs">
                                <div class="card-body">
                                    <ul class="nav nav-pills float-right" role="tablist">
                                        <li class="nav-item">
                                            <a class="nav-link show active" id="prices-tab" data-toggle="pill"
                                               href="#destinations" role="tab" aria-controls="destinations"
                                               aria-expanded="true" aria-selected="true">
                                                <i class="fa fa-euro mr-1"/>
                                                {{ $t("CarrierDestination.prices") }}
                                                <span class="badge badge-light"> {{ carrierDestination.prices.length }} </span>
                                            </a>
                                        </li>
                                    </ul>
                                    <h4 class="header-title m-b-30"> {{$t("CarrierDestination.manage")}} </h4>
                                    <div class="tab-content">
                                        <div class="tab-pane fade show active" id="prices" role="tabpanel" aria-labelledby="prices-tab">
                                            <div class="row">
                                                <CarrierDestinationPriceList :carrier-destination="carrierDestination" :carrier="carrier"
                                                                             @addCarrierDestinationPrice="addCarrierDestinationPrice"
                                                                             @editCarrierDestinationPrice="editCarrierDestinationPrice"
                                                                             @deleteCarrierDestinationPrice="deleteCarrierDestinationPrice"/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PageTitle from "../../../shared/misc/PageTitle";
import ResourceNotFound from "../../../shared/misc/ResourceNotFound";
import APILoading from "../../../shared/API/APILoading";
import CarrierInfoCard from "../../../shared/Billing/Carrier/CarrierInfoCard";
import CarrierDestination from "../../../../classes/Billing/Carrier/CarrierDestination";
import Carrier from "../../../../classes/Billing/Carrier/Carrier";
import CarrierDestinationInfoCard from "./CarrierDestinationInfoCard";
import CarrierDestinationPriceList from "../../../shared/Billing/CarrierDestination/CarrierDestinationPriceList/CarrierDestinationPriceList";
import CarrierDestinationPrice from "../../../../classes/Billing/Carrier/CarrierDestinationPrice";

export default {
    name: "CarrierDestination",
    components: {
        CarrierDestinationPriceList,
        CarrierDestinationInfoCard, CarrierInfoCard, APILoading, ResourceNotFound, PageTitle},
    props: {
        countries: {
            required: true,
        },
    },
    data() {
        return {
            carrierDestination: undefined,
            carrier: undefined,
        };
    },
    computed: {
        ...mapGetters("currencies", {
            currencies: "currencies",
        }),
        pageTitle() {
            const rightLabel = `<span class='pull-right'>
                                    ${this.carrierDestination.carrier.name}
                                    <i class='fas fa-arrow-circle-right text-billing'></i>
                                    ${this.carrierDestination.country.name}
                                    <img class="shadow-sm ml-1" alt="flag" width="40" src="${this.$diceAPI.getBaseUrl()}${this.carrierDestination.country.flagURL}"/>
                                </span>`;
            return `${this.$t("CarrierDestination.carrierDestinationCard")} ${rightLabel}`;
        },
        carrierDestinationId() {
            return parseInt(this.$route.params.id);
        },
    },
    async created() {
        await this.checkUserAuthentication();
        if (!isNaN(this.carrierDestinationId) && this.carrierDestinationId > 0) {
            await this.getCarrierDestination();
        } else {
            this.carrierDestination = null;
        }
    },
    methods: {
        ...mapActions("user", {
            checkUserAuthentication: "checkUserAuthentication",
        }),
        async getCarrierDestination() {
            try {
                let response = await this.$diceAPI.getCarrierDestination(this.carrierDestinationId);
                this.carrierDestination = new CarrierDestination(response.data);
                response = await this.$diceAPI.getCarrier(this.carrierDestination.carrierId);
                this.carrier = new Carrier(response.data);
            } catch (e) {
                if (e.response.status === 404) {
                    this.carrierDestination = null;
                } else {
                    this.$displayError(e);
                }
            }
        },
        editCarrier(carrier) {
            this.carrier = new Carrier(carrier);
        },
        editCarrierDestination(carrierDestination) {
            this.carrierDestination = new CarrierDestination(carrierDestination);
        },
        addCarrierDestinationPrice(carrierDestinationPrice) {
            this.carrierDestination.prices.push(new CarrierDestinationPrice(carrierDestinationPrice));
        },
        editCarrierDestinationPrice(carrierDestinationPrice) {
            const index = this.carrierDestination.prices.findIndex(price => price.id === carrierDestinationPrice.id);
            this.carrierDestination.prices.splice(index, 1, new CarrierDestinationPrice(carrierDestinationPrice));
        },
        deleteCarrierDestinationPrice(carrierDestinationPrice) {
            const index = this.carrierDestination.prices.findIndex(price => price.id === carrierDestinationPrice.id);
            this.carrierDestination.prices.splice(index, 1);
        },
    },
};
</script>

<style scoped>

</style>